import { BadgeProps } from '@dd3tech/wiggui/Badge'
import type { TPropertyTypeV2 } from '../../interfaces'

const LEASE_BADGE: BadgeProps = {
  value: 'RENTA',
  customColors: {
    bgColor: 'bg-notif-turquoise',
    textColor: 'text-white'
  },
  className: 'py-[8px]'
}

const SALE_BADGE: BadgeProps = {
  value: 'VENTA',
  type: 'gray',
  variant: 'dark',
  className: 'py-2'
}

const PRE_SALE_BADGE: BadgeProps = {
  value: 'PREVENTA',
  type: 'gray',
  variant: 'light',
  className: 'py-2'
}

const LEASE_BADGE_V2: BadgeProps = {
  value: 'RENTA',
  customColors: {
    bgColor: 'bg-notif-turquoise',
    textColor: 'text-white'
  }
}

const SALE_BADGE_V2: BadgeProps = {
  value: 'VENTA',
  type: 'green',
  variant: 'dark'
}

const PRE_SALE_BADGE_V2: BadgeProps = {
  value: 'PREVENTA',
  customColors: {
    bgColor: 'bg-notif-blue',
    textColor: 'text-white'
  },
  variant: 'light'
}

export const createBadgesByProperty = ({
  presaleActive,
  saleActive,
  leaseActive,
  isV2 = false
}: {
  presaleActive: boolean
  saleActive: boolean
  leaseActive: boolean
  isV2?: boolean
}) => {
  const badges = []

  if (presaleActive) {
    return [isV2 ? PRE_SALE_BADGE_V2 : PRE_SALE_BADGE]
  }

  if (saleActive) {
    badges.push(isV2 ? SALE_BADGE_V2 : SALE_BADGE)
  }

  if (leaseActive) {
    badges.push(isV2 ? LEASE_BADGE_V2 : LEASE_BADGE)
  }

  return badges
}

interface ParamsGenerateCharacteristicsByProperty {
  dimensions?: string | number
  bedrooms?: string | number
  bathrooms?: string | number
  parkings?: string | number
  groundSize?: string | number
  constructionSize?: string | number
}

export interface Characteristic {
  nameIcon: string
  value: string | number
}

interface ParamsGenerateSimplePropertyTitle {
  propertyType?: TPropertyTypeV2 | null
  saleActive?: boolean
  leaseActive?: boolean
  presaleActive?: boolean
  neighborhood?: string
}

export const generateCharacteristicsByProperty = ({
  dimensions,
  bedrooms,
  bathrooms,
  parkings,
  groundSize,
  constructionSize
}: ParamsGenerateCharacteristicsByProperty) => {
  const characteristics = [
    {
      nameIcon: 'fullscreen',
      value: dimensions ? `${dimensions} m²` : undefined
    },
    {
      nameIcon: 'king_bed',
      value: bedrooms
    },
    {
      nameIcon: 'bathtub',
      value: bathrooms
    },
    {
      nameIcon: 'directions_car',
      value: parkings
    },
    {
      nameIcon: 'aspect_ratio',
      value: groundSize ? `${groundSize} m²` : undefined
    },
    {
      nameIcon: 'foundation',
      value: constructionSize ? `${constructionSize} m²` : undefined
    }
  ]

  const filteredCharacteristics = characteristics.filter(
    (characteristic) =>
      characteristic.value === 0 || Boolean(characteristic.value)
  ) as Characteristic[]

  return filteredCharacteristics
}

export const generateAddress = ({
  neighborhood,
  city
}: {
  neighborhood?: string
  city?: string
}) => {
  if (!neighborhood && !city) {
    return null
  }

  return `${neighborhood && `${neighborhood},`} ${city}`
}

export const PROPERTY_TYPE_DESCRIPTION: Record<TPropertyTypeV2, string> = {
  HOUSE: 'Casa',
  DUPLEX_HOUSE: 'Casa dúplex',
  COUNTRY_HOUSE: 'Quinta Campestre',
  APARTMENT: 'Departamento',
  OFFICE: 'Oficina',
  CONSULTING_ROOM: 'Consultorio',
  WAREHOUSE: 'Bodega',
  INDUSTRIAL_BUILDING: 'Nave industrial',
  SHOP: 'Local',
  SHOP_SHOPPING_CENTER: 'Local en centro comercial',
  TERRAIN: 'Terreno/Lote',
  RANCH: 'Rancho/Hacienda',
  BUILDING: 'Edificio',
  HOTEL: 'Hotel',
  EVENT_HALL: 'Salón/Terraza de eventos',
  COTTAGE: 'Cabaña',
  OTHER: 'Otro',
  APARTMENT_CONDO: 'Departamento en condominio',
  HOUSE_CONDO: 'Casa en condominio',
  HOUSE_RESIDENTIAL: 'Casa residencial'
}

export const generateSimplePropertyTitle = ({
  propertyType,
  saleActive,
  leaseActive,
  presaleActive,
  neighborhood
}: ParamsGenerateSimplePropertyTitle) => {
  const titleArray = []

  if (propertyType) {
    titleArray.push(PROPERTY_TYPE_DESCRIPTION[propertyType])
  }

  switch (true) {
    case leaseActive && saleActive:
      titleArray.push('Venta/Renta')
      break
    case saleActive:
      titleArray.push('Venta')
      break
    case leaseActive:
      titleArray.push('Renta')
      break
    case presaleActive:
      titleArray.push('Preventa')
      break
  }

  if (neighborhood) {
    titleArray.push(neighborhood)
  }

  return titleArray.join(' en ')
}

export const generateCharacteristicsByPropertySecondary = ({
  dimensions,
  bedrooms,
  bathrooms,
  parkings,
  constructionSize
}: ParamsGenerateCharacteristicsByProperty) => {
  const characteristics = [
    {
      nameIcon: 'aspect_ratio',
      value: dimensions ? `${dimensions} m²` : undefined
    },
    {
      nameIcon: 'king_bed',
      value: bedrooms
    },
    {
      nameIcon: 'bathtub',
      value: bathrooms
    },
    {
      nameIcon: 'directions_car',
      value: parkings ? `${parkings} Est.` : undefined
    },
    {
      nameIcon: 'fullscreen',
      value: constructionSize ? `${constructionSize} m²` : undefined
    }
  ]

  const filteredCharacteristics = characteristics.filter(
    (characteristic) =>
      characteristic.value === 0 || Boolean(characteristic.value)
  ) as Characteristic[]

  return filteredCharacteristics
}
